import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * Supports optional mobile sources via `data-video-source-mobile` on each
 * <source>. When the viewport is narrow, the mobile source is used instead of
 * the desktop one, falling back to the desktop source when no mobile source is
 * provided.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    this._mediaQuery = window.matchMedia('(max-width: 749px)');
    this._onMediaChange = () => this._applySources();
    this._mediaQuery.addEventListener('change', this._onMediaChange);

    this._applySources();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._mediaQuery && this._onMediaChange) {
      this._mediaQuery.removeEventListener('change', this._onMediaChange);
    }
  }

  _applySources() {
    const { videoSources, videoElement } = this.refs;
    if (!videoSources || !videoElement) return;

    const mobile = this._mediaQuery ? this._mediaQuery.matches : false;
    let changed = false;

    for (const source of videoSources) {
      const desktop = source.dataset.videoSource || '';
      const mobileSrc = source.dataset.videoSourceMobile || '';
      const next = mobile && mobileSrc ? mobileSrc : desktop;
      if (next && source.getAttribute('src') !== next) {
        source.setAttribute('src', next);
        changed = true;
      }
    }

    if (changed) videoElement.load();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
